import React, {
    Fragment,
    ReactNode,
    RefObject,
    useContext,
    useRef,
} from 'react';

import { LocalizationService } from 'app/service/LocalizationService';

const InternationalizationContext =
    React.createContext<RefObject<I18nContext>>(null);

const REPLACE_SYMBOL = '%s';

/**
 * Используется для получения текстов из бандлов
 * @returns контекст интернационализации
 */
export const useI18nContext = () => {
    const context = useContext(InternationalizationContext);

    return context.current;
};

export class I18nContext {
    localizationService: LocalizationService;

    constructor() {
        this.localizationService = new LocalizationService();
    }

    initialize = async (): Promise<void> => {
        // Проверка для предотвращения повторной загрузки на странице OrderPage в случае захода в приложение через IntroductionPage
        // Загрузка призойдет в случае перехода в заявку по диплинку /c
        if (!this.localizationService.library) {
            await this.localizationService.initialize();
        }
    };

    /**
     * Перевод текста из бандлов по ключу
     * @param key ключ текста, заместители %s в тексте перевода
     * @returns перевод текста
     */
    t = (
        key: string | [string, ...Array<string | ReactNode>],
    ): string | ReactNode => {
        if (Array.isArray(key)) {
            const [keyToTranslate, ...replacers] = key;

            if (!replacers || !replacers.length) {
                return keyToTranslate;
            }

            try {
                if (replacers.some((r) => typeof r !== 'string')) {
                    const parts = this.localizationService
                        .translate(keyToTranslate)
                        .split(REPLACE_SYMBOL);

                    return (
                        <Fragment>
                            {parts.map((part, index) => (
                                <Fragment key={index}>
                                    {part}
                                    {replacers[index]}
                                </Fragment>
                            ))}
                        </Fragment>
                    );
                }

                return replacers.reduce(
                    (result, replacer) =>
                        (result as string).replace(
                            REPLACE_SYMBOL,
                            replacer as string,
                        ),
                    this.localizationService.translate(keyToTranslate),
                );
            } catch (e) {
                return keyToTranslate;
            }
        }

        return this.localizationService.translate(key);
    };
}

export const I18nContextProvider = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const defaultContext = useRef(new I18nContext());

    return (
        <InternationalizationContext.Provider value={defaultContext}>
            {children}
        </InternationalizationContext.Provider>
    );
};
